import React, { useEffect, useMemo } from 'react'
import { useState } from 'react'
import { IOption, IOptions, IPNNVOption } from '../../common/common.types'
import Chips from '../chips/Chips.component'
import { ISegmentButton } from '../segmentButtons/SegmentButtons.component'
import SinglePickList from './singlePickList/SinglePickList.component'
import MutltiPickList from './multiPickList/MultiPickList.component'
import { informationCircle } from 'ionicons/icons'
import PopupComponent from '../infoPopup/Popup.component'
import { TabSegmentButtons } from '../segmentButtons/tabSegmentButtons/TabSegmentButtons.component'
import { IonButton, IonButtons, IonCol, IonGrid, IonIcon, IonRow, useIonViewWillEnter } from '@ionic/react'
import './SegmentedSelect.component.css'
import { useTranslation } from 'react-i18next'

export interface ISegmentOption extends IOption {
  segmentId: number
}
export interface ISegmentPNNVOption extends IPNNVOption {
  segmentId: number
}

export interface ISegmentData extends ISegmentButton {
  type: 'single' | 'multi'
  color?: string
}

export interface ISegmentedSelect {
  segmentsData: ISegmentData[]
  options: IOptions
  values: any[]
  pnnvValue?: ISegmentPNNVOption | null
  infoPopupContent?: React.ReactNode
  commentContent?: React.ReactNode
  onChange: (values: any[], selectedSegment?: ISegmentData, selectedOption?: IOption) => void
  onPNNVChange?: (value: ISegmentPNNVOption | null) => void
  showPNNVs?: boolean
  nemsis?: string
}

const SegmentedSelect: React.FC<ISegmentedSelect> = (props: ISegmentedSelect) => {
  const { t } = useTranslation()
  const {
    options,
    values,
    pnnvValue = null,
    segmentsData,
    infoPopupContent,
    onChange,
    onPNNVChange,
    showPNNVs = true,
    nemsis = 'nemsisCode',
    commentContent,
  } = props
  const [pnnvOptions, setPnnvOptions] = useState<IOptions | null>(null)
  const [selectedValues, setSelectedValues] = useState<any[]>(values)
  const [selectedSegment, setSelectedSegment] = useState<any>(segmentsData[0])
  const [showInfoModal, setShowInfoModal] = useState(false)

  useEffect(() => {
    if (showPNNVs) {
      const obj = options?.filter((data: IOption) => data.isNV || data.isPN)
      setPnnvOptions(obj)
    }
  }, [options])

  const getOptions = showPNNVs ? options.filter((data: IOption) => !data.isNV && !data.isPN) : options

  useIonViewWillEnter(() => {
    setSelectedSegment(segmentsData[0])
  })

  useMemo(() => {
    setSelectedValues(values)
  }, [values])

  const handleChipDeletion = (option: IOption) => {
    const updatedValues = selectedValues.filter((o) => o.id !== option.id)
    setSelectedValues(updatedValues)
    onChange(updatedValues, selectedSegment, option)
    if (onPNNVChange && pnnvValue?.id === option.id) {
      onPNNVChange(null)
    }
  }

  const updateSelectedSegment = (segButton: ISegmentButton) => {
    const segment = segmentsData.find((seg) => seg.id === segButton.id)
    if (segment) setSelectedSegment(segment)
  }

  const addOption = (option: IOption) => {
    const selectedVals = selectedValues.filter((o) => o.segmentId !== selectedSegment?.id)
    selectedVals.push({ ...option, segmentId: selectedSegment?.id })
    setSelectedValues(selectedVals)

    onChange(selectedVals, selectedSegment, option)
    if (onPNNVChange) {
      if (pnnvValue?.segmentId === selectedSegment.id) {
        onPNNVChange(null)
      }
    }
  }

  const addOptions = (selectedOptions: IOptions, selectedOption: IOption) => {
    const selectedVals = selectedValues.filter((o) => o.segmentId !== selectedSegment?.id)
    const selectedValsSeg: ISegmentOption[] = selectedOptions.map((so) => {
      return { ...so, segmentId: selectedSegment?.id }
    })
    selectedVals.push(...selectedValsSeg)
    setSelectedValues(selectedVals)
    onChange(selectedVals, selectedSegment, selectedOption)
    if (onPNNVChange) {
      if (pnnvValue?.segmentId === selectedSegment.id) {
        onPNNVChange(null)
      }
    }
  }

  const addPNNVOption = (option: IOption) => {
    if (onPNNVChange) {
      if (option.name) {
        const obj = {
          id: option.id,
          name: option.name,
          pn: '',
          nv: '',
          segmentId: selectedSegment?.id,
        }
        const key = nemsis as keyof IOption
        if (option[key]) {
          if (option.isPN) {
            obj.pn = String(option[key])
          } else if (option.isNV) {
            obj.nv = String(option[key])
          }
        }
        const filteredSelectedOptions = selectedValues.filter((obj) => obj.segmentId !== selectedSegment?.id)
        onChange(filteredSelectedOptions, selectedSegment)
        setSelectedValues(filteredSelectedOptions)
        onPNNVChange(obj as ISegmentPNNVOption)
      }
    }
  }

  const getSegmetSelectedOptions = (segment: ISegmentData) => {
    const segValues = selectedValues.filter((val) => val.segmentId === segment.id)
    return segValues
  }

  const getSegmetSelectedOption = (segment: ISegmentData) => {
    const segOptions = getSegmetSelectedOptions(segment)
    return segOptions && segOptions.length > 0 ? segOptions[0] : null
  }

  const getSegmetDisabledOptions = (segment: ISegmentData) => {
    const segValues = selectedValues.filter((val) => val.segmentId !== segment.id)
    return segValues
  }

  const getChipsArray = () => {
    let pnnvChip = null
    if (pnnvValue) {
      pnnvChip = { ...pnnvValue, color: segmentsData.find((seg) => seg.id === pnnvValue.segmentId)?.color }
    }
    const chipsArray = selectedValues.map((val) => {
      return {
        ...val,
        color: segmentsData.find((seg) => seg.id === val.segmentId)?.color,
      }
    })
    if (pnnvChip) chipsArray.push(pnnvChip)
    return [...chipsArray].reverse()
  }

  const getPNNVValue = () => {
    if (pnnvValue?.segmentId === selectedSegment.id) {
      return pnnvValue
    }
    return null
  }

  return (
    <>
      <IonGrid id='segment-select-section' className='ion-no-padding'>
        <IonRow
          className='ion-nowrap no-flex-shrink padding-8'
          style={{ height: getChipsArray().length > 0 ? '46px' : '0' }}>
          <Chips chipsArray={getChipsArray()} handleChipDeletion={(option) => handleChipDeletion(option)} />
        </IonRow>
        <IonRow>
          <IonCol className='segment-container ion-no-padding'>
            <TabSegmentButtons
              dataTestId='SegmentButtons'
              selected={selectedSegment}
              segmentsList={segmentsData}
              onChangeAction={(segButton) => updateSelectedSegment(segButton)}
            />
            <div className='icon-wrapper'>
              {infoPopupContent && (
                <IonIcon
                  color='medium'
                  size='large'
                  icon={informationCircle}
                  onClick={() => setShowInfoModal(true)}></IonIcon>
              )}
              {commentContent && commentContent}
            </div>
          </IonCol>
          <hr className='ss-field-divider' />
        </IonRow>
        <IonRow className='overflow-y-auto align-items-start'>
          <IonCol className='ion-no-padding'>
            {segmentsData.map((segment) => {
              return (
                <React.Fragment key={segment.id}>
                  {segment.type === 'single' && segment.id === selectedSegment.id && (
                    <SinglePickList
                      options={getOptions}
                      pnnvOptions={pnnvOptions}
                      disabledOptions={getSegmetDisabledOptions(segment)}
                      selectedOption={getSegmetSelectedOption(segment)}
                      selectedPNNVOption={getPNNVValue()}
                      onChange={addOption}
                      onPNNVChange={addPNNVOption}
                      showPNNVs={showPNNVs}
                    />
                  )}
                  {segment.type === 'multi' && segment.id === selectedSegment.id && (
                    <MutltiPickList
                      options={getOptions}
                      pnnvOptions={pnnvOptions}
                      disabledOptions={getSegmetDisabledOptions(segment)}
                      selectedOptions={getSegmetSelectedOptions(segment)}
                      selectedPNNVOption={getPNNVValue()}
                      selectedColor={selectedSegment.color}
                      onChange={addOptions}
                      onPNNVChange={addPNNVOption}
                      showPNNVs={showPNNVs}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </IonCol>
        </IonRow>
      </IonGrid>

      <PopupComponent
        id='popup-container'
        dataTestId='infoPopupModal'
        isOpen={showInfoModal}
        handleHidePopover={() => setShowInfoModal(false)}
        className='info-popup info-popup-container'>
        {infoPopupContent}
        <IonButtons className='ion-margin'>
          <IonButton
            className='btn btn-secondary'
            expand='block'
            data-testid='Close'
            onClick={() => setShowInfoModal(false)}>
            {t('common.buttons.Close')}
          </IonButton>
        </IonButtons>
      </PopupComponent>
    </>
  )
}

export default SegmentedSelect
